import { api, cacheTimer, thunkLoader, thunks } from "@app/api";
import { createSelector, select } from "@app/fx";
import { extractIdFromLink } from "@app/hal";
import { type WebState, schema } from "@app/schema";
import type {
  DeployLlmTraceDrain,
  DeployLlmTraceDrainResponse,
  HalEmbedded,
  LlmTraceDrainType,
} from "@app/types";
import { ensureLlmPolicy, selectLlmPoliciesByEnvId } from "../llm-policy";

export const deserializeLlmTraceDrain = (
  payload: DeployLlmTraceDrainResponse & { accountId?: string },
): DeployLlmTraceDrain => {
  const links = payload._links;
  const environmentId = payload.accountId || extractIdFromLink(links?.account);
  return {
    id: payload.id,
    handle: payload.handle,
    url: payload.url,
    llmPolicyId: extractIdFromLink(links.llm_policy),
    environmentId,
    drainType: payload.drain_type,
    publicKey: payload.public_key,
    apiKey: payload.api_key,
    status: payload.status,
    createdAt: payload.created_at,
    updatedAt: payload.updated_at,
  };
};

export const selectLlmTraceDrainById = schema.llmTraceDrains.selectById;
export const selectLlmTraceDrainsAsList =
  schema.llmTraceDrains.selectTableAsList;

export const selectLlmTraceDrainsByEnvId = createSelector(
  selectLlmTraceDrainsAsList,
  (state: WebState, props: { envId: string }) =>
    selectLlmPoliciesByEnvId(state, props),
  (_: WebState, props: { envId: string }) => props.envId,
  (drains, policies, envId) => {
    const policyIds = new Set(
      policies.map((p) => p.id).filter((id): id is string => Boolean(id)),
    );
    return drains.filter((d) => {
      if (d.environmentId === envId) return true;
      if (d.llmPolicyId && policyIds.has(d.llmPolicyId)) return true;
      return false;
    });
  },
);

export const fetchAccountLlmTraceDrains = api.get<
  { id: string; orgId: string },
  HalEmbedded<{ llm_trace_drains: DeployLlmTraceDrainResponse[] }>
>(
  "/accounts/:id/llm_trace_drains?organization_id=:orgId",
  {
    supervisor: cacheTimer(),
  },
  function* (ctx, next) {
    const { id, orgId } = ctx.payload;
    if (!id || !orgId) {
      return;
    }

    yield* next();
    if (!ctx.json.ok) {
      return;
    }

    const embedded = ctx.json.value?._embedded?.llm_trace_drains || [];

    const rows = embedded.map((row) =>
      deserializeLlmTraceDrain({ ...row, accountId: id }),
    );
    yield* schema.update(
      schema.llmTraceDrains.add(
        rows.reduce<Record<string, DeployLlmTraceDrain>>((acc, row) => {
          acc[row.id] = row;
          return acc;
        }, {}),
      ),
    );
  },
);

export type { LlmTraceDrainType };

export type CreateLlmTraceDrainPayload = {
  policyId: string;
  orgId: string;
  accountId: string;
  handle: string;
  drainType: LlmTraceDrainType;
} & {
  drainType: "langfuse";
  url: string;
  publicKey: string;
  secretKey: string;
};

function buildCreateLlmTraceDrainBody(payload: CreateLlmTraceDrainPayload) {
  if (payload.drainType === "langfuse") {
    return {
      handle: payload.handle,
      drain_type: payload.drainType,
      url: payload.url,
      public_key: payload.publicKey,
      api_key: payload.secretKey,
      organization_id: payload.orgId,
    };
  }
  return {
    handle: payload.handle,
    drain_type: payload.drainType,
    organization_id: payload.orgId,
  };
}

export const postLlmTraceDrain = api.post<CreateLlmTraceDrainPayload>(
  "/llm_policies/:policyId/llm_trace_drains?organization_id=:orgId",
  function* (ctx, next) {
    ctx.request = ctx.req({
      body: JSON.stringify(buildCreateLlmTraceDrainBody(ctx.payload)),
    });

    yield* next();
    if (!ctx.json.ok) {
      return;
    }

    yield* fetchAccountLlmTraceDrains.run({
      id: ctx.payload.accountId,
      orgId: ctx.payload.orgId,
    });

    ctx.loader = { message: "LLM trace drain created." };
  },
);

export type LlmTraceDrainProps = Omit<CreateLlmTraceDrainPayload, "policyId">;

export const createLlmTraceDrain = thunks.create<LlmTraceDrainProps>(
  "createLlmTraceDrain",
  [
    thunkLoader,
    function* (ctx) {
      const p = ctx.payload;
      const envId = p.accountId;
      const orgId = p.orgId;
      if (!envId || !orgId) {
        return;
      }

      yield* ensureLlmPolicy.run({ envId, orgId });

      const policies = yield* select(selectLlmPoliciesByEnvId, { envId });
      const policy = policies[0];
      const policyId = policy?.id ?? "";
      if (!policyId) {
        ctx.loader = {
          message:
            "Unable to create or load an LLM policy for this environment.",
        };
        return;
      }

      const postCtx = yield* postLlmTraceDrain.run({
        ...p,
        policyId,
      });
      if (!postCtx.json.ok) {
        const data = postCtx.json.error;
        yield* schema.update(
          schema.loaders.error({ id: ctx.key, message: data.message }),
        );
        return;
      }
    },
  ],
);

export const deleteLlmTraceDrain = api.delete<{
  drainId: string;
  orgId: string;
  accountId: string;
}>("/llm_trace_drains/:drainId?organization_id=:orgId", function* (ctx, next) {
  yield* next();
  if (!ctx.json.ok) {
    return;
  }

  const { drainId } = ctx.payload;
  yield* schema.update(schema.llmTraceDrains.remove([drainId]));

  yield* fetchAccountLlmTraceDrains.run({
    id: ctx.payload.accountId,
    orgId: ctx.payload.orgId,
  });

  ctx.loader = { message: "LLM trace drain deleted." };
});
